import { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Family leaderboard.
 *
 * Sibling-safe by design: we never show a "winner" and never compare on
 * outcome metrics like bat speed. Only engagement signals (active days,
 * streak, minutes) — which encourages showing up, not being best.
 *
 * Renders nothing if the household has only one athlete.
 */
export default function FamilyLeaderboard({ theme }) {
  const { token } = useAuth();
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!token) return;
    axios.get(`${API}/household/leaderboard`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => setData(r.data))
      .catch(() => setData(null));
  }, [token]);

  if (!data || !data.rows || data.rows.length < 2) return null;

  return (
    <section
      className="rounded-2xl p-5"
      style={{
        background: theme === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(255,255,255,0.85)',
        border: '1px solid rgba(0,201,167,0.15)',
      }}
    >
      <div className="flex items-baseline justify-between mb-3">
        <h2 className="text-[11px] uppercase tracking-widest opacity-60 font-bold">
          This week, together
        </h2>
        <span className="text-xs opacity-50">{data.rows.length} athletes</span>
      </div>

      <ul className="divide-y" style={{ borderColor: 'rgba(150,160,180,0.15)' }}>
        {data.rows.map((r) => (
          <li key={r.athlete_id} className="py-3 flex items-center gap-3">
            <div className="flex-1 min-w-0">
              <div className="font-semibold truncate">{r.name}</div>
              <div className="text-xs opacity-60 truncate">
                {[r.age_group, r.primary_role].filter(Boolean).join(' · ') || '—'}
              </div>
            </div>
            <Pill label="days" value={`${r.active_days}/7`} />
            <Pill label="streak" value={`🔥 ${r.streak_days}`} />
            <Pill
              label="load"
              value={r.load_flag === 'no_data' ? '—' : r.load_flag}
              color={FLAG_COLORS[r.load_flag]}
            />
          </li>
        ))}
      </ul>

      <p className="text-[10px] opacity-50 mt-3">
        We surface engagement (showing up) — not outcome — to keep this
        encouraging for everyone.
      </p>
    </section>
  );
}

const FLAG_COLORS = {
  ok: '#00C9A7', watch: '#fbbf24', spike: '#ef4444',
  under: '#60a5fa', no_data: '#94a3b8',
};

function Pill({ label, value, color = '#94a3b8' }) {
  return (
    <div
      className="text-center px-2.5 py-1 rounded-lg shrink-0"
      style={{
        background: `${color}22`, border: `1px solid ${color}55`,
        color, minWidth: 56,
      }}
    >
      <div className="text-[9px] uppercase tracking-widest font-bold opacity-80">{label}</div>
      <div className="text-sm font-bold">{value}</div>
    </div>
  );
}
